
import React, { useState, useEffect, useMemo } from 'react';
import Modal from './Modal.jsx';
import UserForm from './UserForm';
import BulkUserForm from './BulkUserForm';
import StatusChip from '../../../components/StatusChip.jsx';
import Toast from '../../../components/Toast.jsx';
// import * as userService from '../../../services/userService.js';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useAuthContext } from '../../../services/authContext';
import { useTableSort, SortableTh } from './useTableSort.jsx';

export default function UserList({ users = null, customerId = null, isAdmin = false, onSelect, onAdd, onAddBulk }) {
    const [modal, setModal] = useState(null); // 'add' | 'bulk' | null
    const [usersState, setUsers] = useState(users || []);
    const [editingUser, setEditingUser] = useState(null);
    const [inviting, setInviting] = useState(false);
    const [inviteResult, setInviteResult] = useState(null);
    // Apollo handles loading
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ message: '', type: 'info', visible: false });
    const [showAssignPanel, setShowAssignPanel] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [assignFilterText, setAssignFilterText] = useState('');
    const { msalInstance, account } = useAuthContext();

    useEffect(() => {
        if (!toast.visible || (toast.type !== 'success' && toast.type !== 'error')) return;
        const delay = toast.type === 'error' ? 5000 : 3000;
        const t = setTimeout(() => setToast(prev => ({ ...prev, visible: false })), delay);
        return () => clearTimeout(t);
    }, [toast.visible, toast.type]);


    // Load users from API
        // Apollo query and mutations for users
        const USERS_QUERY = gql`
            query Users {
                users {
                    id
                    msId
                    email
                    firstName
                    lastName
                    enabled
                    customerIds
                    isAdmin
                    isDeveloper
                    inviteToken
                }
            }
        `;

        const CREATE_USER_MUTATION = gql`
            mutation CreateUser($firstName: String!, $lastName: String!, $email: String!, $enabled: Boolean!, $customerIds: [ID!]) {
                createUser(firstName: $firstName, lastName: $lastName, email: $email, enabled: $enabled, customerIds: $customerIds) {
                    id
                    msId
                    type
                    firstName
                    lastName
                    email
                    enabled
                    groupIds
                    customerIds
                }
            }
        `;

        const UPDATE_USER_MUTATION = gql`
            mutation UpdateUser($updateUserId: ID!, $firstName: String, $lastName: String, $email: String, $enabled: Boolean, $customerIds: [ID!], $isAdmin: Boolean, $isDeveloper: Boolean) {
                updateUser(id: $updateUserId, firstName: $firstName, lastName: $lastName, email: $email, enabled: $enabled, customerIds: $customerIds, isAdmin: $isAdmin, isDeveloper: $isDeveloper) {
                    id
                    msId
                    type
                    firstName
                    lastName
                    email
                    enabled
                    groupIds
                    customerIds
                    isAdmin
                    isDeveloper
                }
            }
        `;

        const DELETE_USER_MUTATION = gql`
            mutation DeleteUser($userId: String!) {
                deleteUser(userId: $userId)
            }
        `;

        const RESEND_INVITE_MUTATION = gql`
            mutation ResendInvite($userId: ID!) {
                resendInvite(userId: $userId)
            }
        `;

        const { data: usersData, loading, error: errorUsers, refetch: refetchUsers } = useQuery(USERS_QUERY);
        const [createUserMutation] = useMutation(CREATE_USER_MUTATION);
        const [updateUserMutation] = useMutation(UPDATE_USER_MUTATION);
        const [deleteUserMutation] = useMutation(DELETE_USER_MUTATION);
        const [resendInviteMutation] = useMutation(RESEND_INVITE_MUTATION);

        useEffect(() => {
            if (users) {
                setUsers(users);
                return;
            }
            if (usersData && usersData.users) {
                setUsers(customerId ? usersData.users.filter(u => u.customerIds?.includes(customerId)) : usersData.users);
            }
        }, [users, usersData, customerId]);

    const { sortedRows: sortedUsers, sortKey, sortDir, toggleSort } = useTableSort(usersState, {
        defaultKey: 'lastName',
        filterFields: ['email', 'firstName', 'lastName'],
        filterText,
    });

    const assignableUsers = useMemo(() => {
        if (!customerId) return [];
        return (usersData?.users ?? []).filter(u => !u.customerIds?.includes(customerId));
    }, [usersData, customerId]);

    const {
        sortedRows: sortedAssignable,
        sortKey: assignSortKey,
        sortDir: assignSortDir,
        toggleSort: toggleAssignSort,
    } = useTableSort(assignableUsers, {
        defaultKey: 'lastName',
        filterFields: ['email', 'firstName', 'lastName'],
        filterText: assignFilterText,
    });

    const handleAdd = () => { setError(null); setEditingUser(null); setModal('add'); };
    const handleAddBulk = () => { setError(null); setEditingUser(null); setModal('bulk'); };
    const handleEdit = (user) => { setError(null); setEditingUser(user); setModal('edit'); };
    const handleClose = () => { setError(null); setModal(null); setEditingUser(null); };

    // Invite logic would be a backend operation; here we just update status for demo
    const handleInvite = async (user) => {
        setError(null);
        // Optionally call a backend invite endpoint here
        setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: 'inviting' } : u));
        await new Promise(res => setTimeout(res, 1000));
        setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: 'invited' } : u));
    };

    const handleInviteAll = async () => {
        setError(null);
        setInviting(true);
        setInviteResult(null);
        const pendingUsers = usersState.filter(u => u.status === 'pending');
        setUsers(prev => prev.map(u => u.status === 'pending' ? { ...u, status: 'inviting' } : u));
        await new Promise(res => setTimeout(res, 1500));
        setUsers(prev => prev.map(u => u.status === 'inviting' ? { ...u, status: 'invited' } : u));
        setInviting(false);
        setInviteResult(`${pendingUsers.length} invitation${pendingUsers.length === 1 ? '' : 's'} sent`);
    };

    // CRUD operations
    const handleSaveUser = async (user) => {
        setError(null);
        handleClose();
        setToast({ message: 'Creating user…', type: 'info', visible: true });
        try {
            const variables = {
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                enabled: user.enabled,
                customerIds: user.customerIds || (customerId ? [customerId] : []),
            };
            const { data } = await createUserMutation({ variables });
            const created = data?.createUser;
            setUsers(usersState => {
                if (usersState.some(u => u.id === created.id)) {
                    return usersState.map(u => u.id === created.id ? created : u);
                }
                return [...usersState, created];
            });
            if (onAdd) onAdd(created);
            refetchUsers();
            setToast({ message: 'User created successfully.', type: 'success', visible: true });
        } catch (e) {
            let msg = e?.graphQLErrors?.[0]?.extensions?.details || e?.graphQLErrors?.[0]?.message || e?.message || 'Failed to create user';
            msg = msg.split(' | MS Graph response:')[0];
            setToast({ message: msg, type: 'error', visible: true });
        }
    };
    const handleSaveEdit = async (user) => {
        setError(null);
        handleClose();
        setToast({ message: 'Saving user…', type: 'info', visible: true });
        try {
            const variables = {
                updateUserId: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                enabled: user.enabled,
                customerIds: user.customerIds || (customerId ? [customerId] : []),
                isAdmin: user.isAdmin,
                isDeveloper: user.isDeveloper,
            };
            const { data } = await updateUserMutation({ variables });
            const updated = data?.updateUser;
            setUsers(prev => prev.map(u => u.id === user.id ? updated : u));
            if (onSelect) onSelect(updated);
            refetchUsers();
            setToast({ message: 'User updated successfully.', type: 'success', visible: true });
        } catch (e) {
            let msg = e?.graphQLErrors?.[0]?.extensions?.details || e?.graphQLErrors?.[0]?.message || e?.message || 'Failed to update user';
            msg = msg.split(' | MS Graph response:')[0];
            setToast({ message: msg, type: 'error', visible: true });
        }
    };
    const handleSaveBulk = async (bulkUsers) => {
        setError(null);
        handleClose();
        setToast({ message: `Saving ${bulkUsers.length} user${bulkUsers.length === 1 ? '' : 's'}…`, type: 'info', visible: true });
        const createdUsers = [];
        try {
            for (const u of bulkUsers) {
                const variables = {
                    firstName: u.firstName,
                    lastName: u.lastName,
                    email: u.email,
                    enabled: true,
                    customerIds: u.customerIds || (customerId ? [customerId] : []),
                };
                const { data } = await createUserMutation({ variables });
                createdUsers.push(data?.createUser);
            }
            setUsers(prev => {
                const createdIds = new Set(createdUsers.map(u => u.id));
                return [...prev.filter(u => !createdIds.has(u.id)), ...createdUsers];
            });
            if (onAddBulk) onAddBulk(createdUsers);
            refetchUsers();
            setToast({ message: `${createdUsers.length} user${createdUsers.length === 1 ? '' : 's'} created successfully.`, type: 'success', visible: true });
        } catch (e) {
            let msg = e?.graphQLErrors?.[0]?.extensions?.details || e?.graphQLErrors?.[0]?.message || e?.message || 'Failed to create user';
            msg = msg.split(' | MS Graph response:')[0];
            const successCount = createdUsers.length;
            const failedUser = bulkUsers[successCount];
            if (successCount > 0) {
                msg = `Created ${successCount} of ${bulkUsers.length} user(s). Failed on ${failedUser?.email}: ${msg}`;
            } else {
                msg = `Failed to create ${failedUser?.email}: ${msg}`;
            }
            setToast({ message: msg, type: 'error', visible: true });
        }
    };
    const handleShowInvite = (user) => {
        if (!user.inviteToken) {
            setToast({ message: 'No active invite token for this user.', type: 'error', visible: true });
            return;
        }
        const url = `${window.location.origin}/invite/${user.inviteToken}`;
        navigator.clipboard.writeText(url).then(() => {
            setToast({ message: 'Invite URL copied to clipboard.', type: 'success', visible: true });
        }).catch(() => {
            // Fallback: show the URL in the toast so they can copy manually
            setToast({ message: url, type: 'info', visible: true });
        });
    };

    const handleResendInvite = async (user) => {
        try {
            await resendInviteMutation({ variables: { userId: user.id } });
            setToast({ message: `Invite resent to ${user.email}.`, type: 'success', visible: true });
        } catch (e) {
            setError('Failed to resend invite');
        }
    };

    const handleDeleteUser = async (user) => {
        handleClose();
        try {
            const { data } = await deleteUserMutation({ variables: { userId: user.id } });
            if (!data?.deleteUser) {
                throw new Error('Delete operation failed');
            }
            setUsers(prev => prev.filter(u => u.id !== user.id));
            refetchUsers();
        } catch (e) {
            setError('Failed to delete user');
        }
    };

    const handleRemoveFromCompany = async (user) => {
        setError(null);
        try {
            const newCustomerIds = (user.customerIds || []).filter(id => id !== customerId);
            await updateUserMutation({ variables: { updateUserId: user.id, customerIds: newCustomerIds } });
            setUsers(prev => prev.filter(u => u.id !== user.id));
            refetchUsers();
            setToast({ message: `${user.email} removed from company.`, type: 'success', visible: true });
        } catch (e) {
            setError('Failed to remove user from company');
        }
    };

    const handleAssignToCompany = async (user) => {
        setError(null);
        try {
            const newCustomerIds = [...(user.customerIds || []), customerId];
            await updateUserMutation({ variables: { updateUserId: user.id, customerIds: newCustomerIds } });
            await refetchUsers();
            setToast({ message: `${user.email} assigned to company.`, type: 'success', visible: true });
        } catch (e) {
            setError('Failed to assign user to company');
        }
    };

    return (
        <div>
            <Toast message={toast.message} visible={toast.visible} type={toast.type} />
            <div className="users-header">
                <h3>Users</h3>
                <input
                    type="text"
                    placeholder="Search…"
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                    className="admin-quick-filter"
                />
                {/* Users are added from a customer so they are always linked to one; the global All Users tab is view/edit only. */}
                {!loading && customerId && (
                    <>
                        <button onClick={handleAdd} className="btn btn-primary">Add User</button>
                        <button onClick={handleAddBulk} className="btn btn-secondary">Add Users in Bulk</button>
                        <button onClick={() => setShowAssignPanel(p => !p)} className="btn btn-secondary">
                            {showAssignPanel ? 'Cancel' : 'Assign User'}
                        </button>
                    </>
                )}
            </div>
            {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
            {errorUsers && <div style={{ color: 'red', marginBottom: 8 }}>{errorUsers.message}</div>}
            {loading ? (
                <div>Loading users...</div>
            ) : !customerId && (
                <div style={{ marginBottom: 8, fontStyle: 'italic' }}>
                    Click an action to manage a user.
                </div>
            )}
            <table className="admin-table">
                <thead>
                    <tr>
                        <SortableTh label="Email"      sortKeyName="email"     sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                        <SortableTh label="First Name" sortKeyName="firstName" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                        <SortableTh label="Last Name"  sortKeyName="lastName"  sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                        <SortableTh label="Enabled"    sortKeyName="enabled"   sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {sortedUsers.map((user) => (
                        <tr key={user.id}>
                            <td>{user.email}</td>
                            <td>{user.firstName}</td>
                            <td>{user.lastName}</td>
                            <td><StatusChip status={user.enabled} labels={{ true: 'Active', false: 'Disabled' }} /></td>
                            <td>
                                <>
                                    <button onClick={() => handleEdit(user)} className="btn-edit">Edit</button>
                                    <button onClick={() => handleResendInvite(user)} style={{ marginLeft: 8 }} className="btn-edit">
                                        Resend Invite
                                    </button>
                                    <button onClick={() => handleShowInvite(user)} style={{ marginLeft: 8 }} className="btn-edit">
                                        Copy Invite Link
                                    </button>
                                    {customerId && (
                                        <button onClick={() => handleRemoveFromCompany(user)} style={{ marginLeft: 8 }} className="btn-edit">
                                            Remove
                                        </button>
                                    )}
                                </>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {customerId && showAssignPanel && (
                <div style={{ marginTop: 16, border: '1px solid #ddd', borderRadius: 6, padding: 12 }}>
                    <div className="users-header" style={{ marginBottom: 12 }}>
                        <h4 style={{ margin: 0 }}>Assign Existing User</h4>
                        <input
                            type="text"
                            placeholder="Search…"
                            value={assignFilterText}
                            onChange={e => setAssignFilterText(e.target.value)}
                            className="admin-quick-filter"
                        />
                    </div>
                    {assignableUsers.length === 0 ? (
                        <div style={{ fontStyle: 'italic' }}>All users are already assigned to this company.</div>
                    ) : (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <SortableTh label="Email"      sortKeyName="email"     sortKey={assignSortKey} sortDir={assignSortDir} onClick={toggleAssignSort} />
                                    <SortableTh label="First Name" sortKeyName="firstName" sortKey={assignSortKey} sortDir={assignSortDir} onClick={toggleAssignSort} />
                                    <SortableTh label="Last Name"  sortKeyName="lastName"  sortKey={assignSortKey} sortDir={assignSortDir} onClick={toggleAssignSort} />
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedAssignable.map(u => (
                                    <tr key={u.id}>
                                        <td>{u.email}</td>
                                        <td>{u.firstName}</td>
                                        <td>{u.lastName}</td>
                                        <td>
                                            <button onClick={() => handleAssignToCompany(u)} className="btn-edit">Assign</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            <div style={{ marginTop: 16 }}>
                {(customerId && usersState.filter(u => u.status === 'pending').length > 0 && !inviting) && (
                    <button onClick={handleInviteAll}>
                        Invite all Pending
                    </button>
                )}
                {inviting && (
                    <button disabled>Inviting All...</button>
                )}
                {inviteResult && <span style={{ marginLeft: 16 }}>{inviteResult}</span>}
            </div>

            <Modal open={modal === 'add'} onClose={handleClose}>
                <UserForm user={customerId ? { customerIds: [customerId] } : {}} customers={[]} onSave={handleSaveUser} onCancel={handleClose} onDelete={handleDeleteUser} />
            </Modal>
            <Modal open={modal === 'edit'} onClose={handleClose}>
                <UserForm user={editingUser} customers={[]} onSave={handleSaveEdit} onCancel={handleClose} onDelete={handleDeleteUser} />
            </Modal>
            <Modal open={modal === 'bulk'} onClose={handleClose}>
                <BulkUserForm onSave={handleSaveBulk} onCancel={handleClose} />
            </Modal>
        </div>
    );
}
